'use client';

import { useMemo } from 'react';

import { QuotedColorGroup, SmallLotPricing } from '@ravago/shared/page-data/models/elements';
import { Address } from '@ravago/shared/page-data/models/elements/address.model';
import { LeadTimeIndicator } from '@ravago/shared/page-data/models/elements/product-lead-time.model';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { Divider } from '@ravago/shared/radiance/components/divider/components/Divider/Divider';
import { ColorIcon } from '@ravago/shared/radiance/components/icon/components/ColorIcon/ColorIcon';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { Link } from '@ravago/shared/radiance/components/link/components/Link/Link';
import { RadioCard } from '@ravago/shared/radiance/components/radio/components/RadioCard/RadioCard';
import { RadioCardGroup } from '@ravago/shared/radiance/components/radio/components/RadioCardGroup/RadioCardGroup';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { LeadTimeBadge } from '../../../../shared/lead-time-badge/lead-time-badge';
import { hasQuotedColorGroupMtsLocalLeadTime, hasSmallLotMtsLocalLeadTime } from '../../../../../utils/lead-time';
import { DEFAULT_PRODUCER_COLOR_CODE } from '../../constants';
import { CallOffGroup, getSmallLotPricingForVariant, UniqueSmallLotColor } from '../../utils/order-now-utils';

import { SmallLotColorSelector } from './small-lot-color-selector';

const getButtonLabelKey = (
   isCallOffSelected: boolean,
   isQuotedColorSelected: boolean,
   isSmallLotSelected: boolean,
   selectedColorGroup: QuotedColorGroup | undefined
): string => {
   if (isCallOffSelected) return 'dynamic-product-block.view-call-off';
   if (isQuotedColorSelected) {
      return selectedColorGroup && selectedColorGroup.quotedColors.length > 1
         ? 'dynamic-product-block.view-quotes'
         : 'dynamic-product-block.view-quote';
   }
   if (isSmallLotSelected) return 'dynamic-product-block.small-lot.view-prices';
   return 'dynamic-product-block.select-a-color';
};

interface Props {
   callOffGroups: CallOffGroup[];
   quotedColorGroups: QuotedColorGroup[];
   smallLotPrices: SmallLotPricing[];
   smallLotPricesForLeadTime?: SmallLotPricing[];
   smallLotDefaultAddress?: Address;
   uniqueSmallLotColors: UniqueSmallLotColor[];
   colorCodesForSelectedColor: string[];
   selectedCallOffKey: string | undefined;
   selectedColorGroupKey: string | undefined;
   selectedSmallLotColorId: number | undefined;
   selectedSmallLotProducerColorCode: string | undefined;
   leadTimeIndicators: LeadTimeIndicator[];
   leadTimeEnabled: boolean;
   bracketPricingEnabled?: boolean;
   onCallOffGroupChange: (key: string) => void;
   onColorGroupChange: (key: string) => void;
   onSmallLotColorChange: (colorId: number) => void;
   onSmallLotColorCodeChange: (code: string) => void;
   onViewCallOff: () => void;
   onViewQuote: () => void;
   onViewPrices: () => void;
   onRequestQuote: () => void;
}

export const OrderNowV2Selector = ({
   callOffGroups,
   quotedColorGroups,
   smallLotPrices,
   smallLotPricesForLeadTime = smallLotPrices,
   smallLotDefaultAddress,
   uniqueSmallLotColors,
   colorCodesForSelectedColor,
   selectedCallOffKey,
   selectedColorGroupKey,
   selectedSmallLotColorId,
   selectedSmallLotProducerColorCode,
   leadTimeIndicators,
   leadTimeEnabled,
   bracketPricingEnabled = false,
   onCallOffGroupChange,
   onColorGroupChange,
   onSmallLotColorChange,
   onSmallLotColorCodeChange,
   onViewCallOff,
   onViewQuote,
   onViewPrices,
   onRequestQuote
}: Props) => {
   const translate = useTranslations();

   const isCallOffSelected = selectedCallOffKey !== undefined;
   const isQuotedColorSelected = selectedColorGroupKey !== undefined;
   const isSmallLotSelected = selectedSmallLotColorId !== undefined && selectedSmallLotProducerColorCode !== undefined;
   const canProceed = isCallOffSelected || isQuotedColorSelected || isSmallLotSelected;

   const quoteMtsLocalMap = useMemo(() => {
      if (!leadTimeEnabled) return {};

      const map: Record<string, boolean> = {};
      for (const group of quotedColorGroups) {
         const quoteHasMtsLocal = hasQuotedColorGroupMtsLocalLeadTime(leadTimeIndicators, group, { bracketPricingEnabled });
         const smallLotPricing = getSmallLotPricingForVariant(smallLotPricesForLeadTime, group.colorId, group.producerColorCode);
         const smallLotHasMtsLocal =
            smallLotDefaultAddress !== undefined && smallLotPricing !== null
               ? hasSmallLotMtsLocalLeadTime(leadTimeIndicators, smallLotPricing, smallLotDefaultAddress)
               : false;

         map[group.key] = quoteHasMtsLocal || smallLotHasMtsLocal;
      }
      return map;
   }, [quotedColorGroups, smallLotPricesForLeadTime, smallLotDefaultAddress, leadTimeIndicators, leadTimeEnabled, bracketPricingEnabled]);
   const hasMtsLocalLeadTime = Object.values(quoteMtsLocalMap).some(Boolean);

   const selectedColorGroup = quotedColorGroups.find((group) => group.key === selectedColorGroupKey);

   const handleProceed = () => {
      if (isCallOffSelected) onViewCallOff();
      else if (isQuotedColorSelected) onViewQuote();
      else if (isSmallLotSelected) onViewPrices();
   };

   return (
      <Box direction="column">
         {callOffGroups.length > 0 && (
            <Box direction="column" gap="xs">
               <Typography variant="body-highlight" color="subtle">
                  {translate('dynamic-product-block.call-off')}
               </Typography>
               <RadioCardGroup value={selectedCallOffKey ?? ''} onChange={onCallOffGroupChange}>
                  {callOffGroups.map((group) => (
                     <RadioCard key={group.key} value={group.key}>
                        <Box justify="between" items="center">
                           <Box gap="sm" items="center">
                              <ColorIcon colorName={group.colorName.toLowerCase()} />
                              <Typography>{group.colorName.toUpperCase()}</Typography>
                              {group.producerColorCode !== DEFAULT_PRODUCER_COLOR_CODE && (
                                 <>
                                    <Divider orientation="vertical" />
                                    <Typography>{group.producerColorCode}</Typography>
                                 </>
                              )}
                           </Box>
                        </Box>
                     </RadioCard>
                  ))}
               </RadioCardGroup>
            </Box>
         )}

         {quotedColorGroups.length > 0 && (
            <Box direction="column" gap="xs">
               <Typography variant="body-highlight" color="subtle">
                  {translate('dynamic-product-block.quotes')}
               </Typography>
               <RadioCardGroup value={selectedColorGroupKey ?? ''} onChange={onColorGroupChange}>
                  {quotedColorGroups.map((group) => (
                     <RadioCard key={group.key} value={group.key}>
                        <Box justify="between" items="center">
                           <Box gap="sm" items="center">
                              <ColorIcon colorName={group.colorName.toLowerCase()} />
                              <Typography>{group.colorName.toUpperCase()}</Typography>
                              {group.producerColorCode !== DEFAULT_PRODUCER_COLOR_CODE && (
                                 <>
                                    <Divider orientation="vertical" />
                                    <Typography>{group.producerColorCode}</Typography>
                                 </>
                              )}
                           </Box>
                           <Box gap="sm" items="center">
                              {hasMtsLocalLeadTime && <LeadTimeBadge isMtsLocal={quoteMtsLocalMap[group.key]} />}
                           </Box>
                        </Box>
                     </RadioCard>
                  ))}
               </RadioCardGroup>
            </Box>
         )}

         {uniqueSmallLotColors.length > 0 && (
            <SmallLotColorSelector
               smallLotPrices={smallLotPrices}
               smallLotDefaultAddress={smallLotDefaultAddress}
               hasQuotes={quotedColorGroups.length > 0}
               hasMtsLocalLeadTime={hasMtsLocalLeadTime}
               uniqueSmallLotColors={uniqueSmallLotColors}
               colorCodesForSelectedColor={colorCodesForSelectedColor}
               selectedColorId={selectedSmallLotColorId}
               selectedProducerColorCode={selectedSmallLotProducerColorCode}
               leadTimeIndicators={leadTimeIndicators}
               leadTimeEnabled={leadTimeEnabled}
               onColorChange={onSmallLotColorChange}
               onProducerColorCodeChange={onSmallLotColorCodeChange}
            />
         )}

         <Button fullWidth disabled={!canProceed} onClick={handleProceed}>
            {translate(getButtonLabelKey(isCallOffSelected, isQuotedColorSelected, isSmallLotSelected, selectedColorGroup))}
         </Button>

         <Box justify="center" items="center" gap="xs">
            <Box gap="3xs">
               <Icon icon="info-circle" color="subtle" />
               <Typography component="span" color="subtle">
                  {translate('dynamic-product-block.small-lot.dont-see-your-color')}
               </Typography>
            </Box>
            <Link onClick={onRequestQuote}>{translate('dynamic-product-block.small-lot.request-quote')}</Link>
         </Box>
      </Box>
   );
};
